import type { Business, ScoreSnapshot } from '../contract';
import { MapPinIcon } from 'lucide-react';
import { PillarBars } from '../components/PillarBars';
import { joinDotted, monthLabel, shortDateOrNull } from '../lib/oura';
import type { LiveHeadline } from '../liveAudit';
import { cn } from '../lib/cn';
import type { AeoAnswerCoverage, AeoAreaScore, AeoLevelCap, AeoSentiment } from '@service/aeo';

/**
 * The four report metrics from ourteam-backend#3802. Every one of them is
 * `null` when it could not be measured on this run, and each renders as an
 * explicit "not measured" rather than a zero — this document is what a client
 * reads, and a zero here is a claim we did not earn.
 */
export interface ReportMetrics {
  areaScores?: AeoAreaScore[];
  maturityLevel?: number | null;
  maturityLabel?: string | null;
  /** Why the level is held where it is. `null` or absent = not capped. */
  levelCappedBy?: AeoLevelCap | null;
  /** 0-10. */
  aiReadability?: number | null;
  sentiment?: AeoSentiment | null;
  answerCoverage?: AeoAnswerCoverage | null;
}

/** The cap, in the client's words. The level itself is interpolated, so the
 *  line cannot disagree with the number printed above it. */
const LEVEL_CAP_LINE: Record<AeoLevelCap, string> = {
  site_health: 'your site health is below 50.',
  rented_visibility: 'AI mentions you through directories, not your own website.',
};

/** 0-10 to the platform's 5-stop scale. */
function areaTone(score: number | null): string {
  if (score === null) return 'bg-gray-200';
  if (score >= 8) return 'bg-emerald-500';
  if (score >= 6) return 'bg-blue-500';
  if (score >= 4) return 'bg-amber-500';
  return 'bg-rose-500';
}

/**
 * One frozen snapshot. No navigation, no interactivity. The same component is
 * the cold-email attachment, the monthly client report, and the agency's
 * white-labelled deliverable — hence the optional logo and accent (§Report).
 */
export function Report({
  snapshot, business, logo = 'ourteam', metrics, live,
}: {
  snapshot: ScoreSnapshot;
  business: Business;
  logo?: string;
  /** Real report metrics (#3802). Absent on a fixture business, and on any run
   *  finalized before that ticket shipped — the sections below simply don't
   *  render rather than showing empty scaffolding. */
  metrics?: ReportMetrics;
  /**
   * Present for a live scan, absent for a fixture business. A live snapshot is
   * a shell with placeholders (no level, `plusMinus: 0`, no best Google
   * position), so with this set the page prints only what the scan measured
   * and leaves out what it does not know (#4238).
   */
  live?: LiveHeadline;
}) {
  const { headline } = snapshot;
  const level = live
    ? (metrics?.maturityLevel != null && metrics.maturityLabel ? `Level ${metrics.maturityLevel} · ${metrics.maturityLabel}` : null)
    : `Level ${snapshot.level} · ${snapshot.levelName}`;
  const checkedOn = shortDateOrNull(snapshot.capturedAt);
  const namedCount = Math.round(headline.namedRate * headline.questionsTested);
  const topFixes = snapshot.actions.filter((a) => a.status !== 'done').slice(0, 3);

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        {/* Accent header */}
        <div className="flex items-center justify-between bg-blue-600 px-8 py-5 text-white">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/15">
              <MapPinIcon className="h-4 w-4" />
            </span>
            <span className="text-base font-bold">Oura</span>
          </div>
          <span className="text-sm font-medium text-blue-100">Prepared by {logo}</span>
        </div>

        <div className="px-8 py-8">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            Visibility report · {monthLabel(snapshot.capturedAt.slice(0, 7))}
          </p>
          <h1 className="mt-1 text-2xl font-bold text-gray-900">{business.name}</h1>
          <p className="text-sm text-gray-500">{joinDotted(business.vertical, business.city)}</p>

          <div className="mt-6 flex flex-wrap items-end gap-x-8 gap-y-4 border-y border-gray-100 py-6">
            <div>
              <div className="flex items-baseline gap-1.5">
                {/* A live scan prints its own AEO score. The snapshot's `total` blends
                    AEO and site health into a number shown nowhere else, and its
                    plusMinus is a placeholder 0 (#4238). */}
                <span className="text-5xl font-bold text-gray-900 tabular-nums">{live ? live.aeoScore ?? '—' : snapshot.total ?? '—'}</span>
                {live && live.aeoScore !== null && (
                  <span className="text-xl font-medium text-gray-400 tabular-nums">/ 100</span>
                )}
                {!live && snapshot.total !== null && (
                  <span className="text-xl font-medium text-gray-400 tabular-nums">± {snapshot.plusMinus}</span>
                )}
              </div>
              {live && <p className="mt-1 text-xs font-medium uppercase tracking-wide text-gray-400">AEO score</p>}
              {level && <p className="mt-1 text-sm font-medium text-blue-700">{level}</p>}
            </div>
            <div className="text-sm text-gray-600">
              {/* Absent is not zero: a scan with no count prints no line, never "0 of 0" (#4154). */}
              {headline.questionsTested > 0 && (
                <p>Named by AI in <b className="text-gray-900">{namedCount} of {headline.questionsTested}</b> questions</p>
              )}
              {/* "Not in the top 20" is a finding, and a scan without Search Console
                  has none to report. Connected, this page still reads no position,
                  so it says nothing rather than guess (#4238). */}
              {live ? (
                live.searchConnected !== true && (
                  <p className="mt-1">Google search: <b className="text-gray-900">not measured</b></p>
                )
              ) : (
                <p className="mt-1">
                  Best Google spot{' '}
                  <b className="text-gray-900">
                    {headline.bestGooglePosition !== null ? `#${headline.bestGooglePosition}` : 'not in the top 20'}
                  </b>
                </p>
              )}
            </div>
          </div>

          {headline.plainSummary && (
            <p className="mt-6 text-sm leading-relaxed text-gray-700">{headline.plainSummary}</p>
          )}

          {snapshot.pillars.length > 0 && (
            <div className="mt-8">
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Where you stand</h2>
              <PillarBars pillars={snapshot.pillars} />
            </div>
          )}

          {/* ── Real report metrics (ourteam-backend#3802) ────────────────── */}
          {!!metrics?.areaScores?.length && (
            <div className="mt-8">
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
                The four things that decide whether AI recommends you
              </h2>
              <div className="space-y-2.5">
                {metrics.areaScores.map((area) => (
                  <div key={area.key} className="flex items-center gap-3">
                    <span className="w-44 flex-shrink-0 text-sm text-gray-700">{area.label}</span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className={cn('h-full rounded-full', areaTone(area.score_10))}
                        style={{ width: area.score_10 !== null ? `${area.score_10 * 10}%` : '0%' }}
                      />
                    </div>
                    {/* `measured: false` is not a bad score — it is no score. */}
                    <span className="w-20 flex-shrink-0 text-right text-sm tabular-nums text-gray-900">
                      {area.measured && area.score_10 !== null
                        ? `${area.score_10.toFixed(1)} / 10`
                        : <span className="text-xs text-gray-400">Not measured</span>}
                    </span>
                  </div>
                ))}
              </div>
              {metrics.maturityLabel && (
                <p className="mt-3 text-sm text-gray-600">
                  Overall: <b className="text-gray-900">Level {metrics.maturityLevel} · {metrics.maturityLabel}</b>
                </p>
              )}
              {/* Without the reason a capped level reads as an arbitrary score,
                  or a 4 as "nearly there" (#3785). Only a known cap prints. */}
              {metrics.maturityLabel && metrics.levelCappedBy && LEVEL_CAP_LINE[metrics.levelCappedBy] && (
                <p className="mt-1 text-sm text-gray-500">
                  Held at Level {metrics.maturityLevel}: {LEVEL_CAP_LINE[metrics.levelCappedBy]}
                </p>
              )}
            </div>
          )}

          {(metrics?.sentiment || metrics?.aiReadability !== undefined || metrics?.answerCoverage) && (
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {metrics?.sentiment && (
                <div className="rounded-lg border border-gray-200 px-4 py-3">
                  <p className="text-xs uppercase tracking-wide text-gray-400">How AI describes you</p>
                  <p className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">
                    {metrics.sentiment.score}<span className="text-base text-gray-400"> / 100</span>
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Across {metrics.sentiment.mentionsScored} answer{metrics.sentiment.mentionsScored === 1 ? '' : 's'} that named you ·{' '}
                    {metrics.sentiment.breakdown.positive} positive, {metrics.sentiment.breakdown.neutral} neutral,{' '}
                    {metrics.sentiment.breakdown.negative} negative
                  </p>
                </div>
              )}
              {metrics?.aiReadability !== null && metrics?.aiReadability !== undefined && (
                <div className="rounded-lg border border-gray-200 px-4 py-3">
                  <p className="text-xs uppercase tracking-wide text-gray-400">How clearly you explain</p>
                  <p className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">
                    {metrics.aiReadability.toFixed(1)}<span className="text-base text-gray-400"> / 10</span>
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Whether an assistant can read your pages and repeat what you do.
                  </p>
                </div>
              )}
              {metrics?.answerCoverage && (
                <div className="rounded-lg border border-gray-200 px-4 py-3">
                  <p className="text-xs uppercase tracking-wide text-gray-400">Questions your site answers</p>
                  <p className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">
                    {metrics.answerCoverage.coveragePct}<span className="text-base text-gray-400">%</span>
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    {metrics.answerCoverage.answered} of {metrics.answerCoverage.total} customer questions
                    have a page that answers them.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* The gaps are the most actionable thing in the whole report: each one
              is a question customers ask that the site never answers. */}
          {!!metrics?.answerCoverage?.gaps?.length && (
            <div className="mt-8">
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
                Questions customers ask that your site doesn't answer
              </h2>
              <ul className="space-y-1.5">
                {metrics.answerCoverage.gaps.slice(0, 6).map((gap) => (
                  <li key={gap.query} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-rose-400" />
                    <span>{gap.query}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* A heading over an empty list reads as a finding with nothing in it (#4238). */}
          {topFixes.length > 0 && (
          <div className="mt-8">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
              The fixes that move it most next month
            </h2>
            <ol className="space-y-2.5">
              {topFixes.map((a, i) => (
                <li key={a.id} className="flex items-start gap-3">
                  <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                    {i + 1}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{a.title}</p>
                    <p className="text-sm text-gray-500">{a.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          )}

          {/* A live report says only what its scan measured: a footer naming Google and
              reviews as checked would contradict "Google search: not measured" (#4238). */}
          <p className="mt-8 border-t border-gray-100 pt-4 text-xs text-gray-400">
            {live ? (
              <>
                This report shows only what the latest scan measured{checkedOn ? ` on ${checkedOn}` : ''}.
                Anything not measured says so or is left out.
              </>
            ) : (
              <>
                Every number here comes from a live check of your website, the AI engines, Google, and your
                reviews{checkedOn ? ` on ${checkedOn}` : ''}. Nothing is estimated unless it says so.
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
